import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Users, MousePointerClick, MailOpen, ExternalLink, AlertCircle,
  Plus, Pencil, Trash2, CheckCircle2, Send, Clock,
} from "lucide-react";
import { formatDistanceToNow, parseISO, format } from "date-fns";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardMeta } from "@/components/DashboardMeta";

const BRAND = { terracotta: "#B2674A", sage: "#AEBDB1", gold: "#C69E5F", primary: "#425252" };

const STATUS_META = {
  pending: { label: "Pending", color: "bg-peach/35 text-terracotta" },
  approved: { label: "Approved", color: "bg-sage/30 text-brand-primary" },
  added_to_newsletter: { label: "Added to newsletter", color: "bg-terracotta/15 text-terracotta" },
};

export default function Mailchimp() {
  const { user } = useAuth();
  const canChangeStatus = user?.role === "admin" || !!user?.newsletter_editor;

  const [data, setData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // submission being edited
  const [form, setForm] = useState({ title: "", body: "", notes: "" });

  const load = async ({ force = false, silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [m, s] = await Promise.all([
        api.get(`/mailchimp/summary${force ? "?nocache=1" : ""}`),
        api.get("/newsletter-submissions"),
      ]);
      setData(m.data);
      setSubmissions(s.data);
      setLoadError(null);
    } catch (e) {
      if (!silent) setLoadError(e?.response?.data?.detail || "Failed to load Mailchimp data");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Silent background refresh every 60s. Uses the cached endpoint (no ?nocache=1)
  // so the backend's 60s cache absorbs the repeat hits while still bumping the
  // "Last updated" label once the cache expires. `silent:true` keeps the spinner
  // off so nothing flickers while the user is reading. Cleaned up on unmount.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      load({ silent: true });
    }, 60_000);
    return () => clearInterval(id);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Force a fresh fetch the moment the tab regains focus so staff returning
  // from lunch see up-to-date numbers without having to click Refresh.
  // Uses `?nocache=1` to bypass the server cache, but `silent:true` so the
  // UI doesn't flash a spinner. Only fires on the hidden → visible transition.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        load({ force: true, silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", body: "", notes: "" });
    setFormOpen(true);
  };
  const openEdit = (sub) => {
    setEditing(sub);
    setForm({ title: sub.title, body: sub.body, notes: sub.notes || "" });
    setFormOpen(true);
  };

  const submitForm = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/newsletter-submissions/${editing.id}`, {
          title: form.title,
          body: form.body,
          notes: form.notes || null,
        });
        toast.success("Submission updated");
      } else {
        await api.post("/newsletter-submissions", {
          title: form.title,
          body: form.body,
          notes: form.notes || null,
        });
        toast.success("Submission added");
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const changeStatus = async (sub, next) => {
    try {
      await api.patch(`/newsletter-submissions/${sub.id}/status`, { status: next });
      toast.success(`Status → ${STATUS_META[next].label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to change status");
    }
  };

  const deleteSub = async (sub) => {
    if (!confirm(`Delete "${sub.title}"?`)) return;
    try {
      await api.delete(`/newsletter-submissions/${sub.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to delete");
    }
  };

  if (!data) {
    return (
      <div className="space-y-8" data-testid="mailchimp-loading">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Overall engagement</p>
          <h3 className="font-heading text-2xl text-brand-primary mt-1">Loading Mailchimp…</h3>
        </div>
        {loadError ? (
          <Card className="p-6 rounded-2xl bg-white border border-terracotta/20 text-brand-primary/70" data-testid="mailchimp-load-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" />
              <div>
                <p className="font-medium text-brand-primary">Couldn't load Mailchimp data</p>
                <p className="text-sm mt-1">{loadError}</p>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-36 rounded-2xl" />
            ))}
          </div>
        )}
      </div>
    );
  }

  const connected = data.source === "mailchimp";
  const errored = data.source === "error";
  const disconnected = data.source === "not_connected";

  const eng = data.engagement_30d || { total_sends: 0, open_rate: 0, click_rate: 0, by_type: {}, window_days: 30 };
  const cards = [
    {
      label: "Emails sent",
      value: (eng.total_sends || 0).toLocaleString(),
      sub: `last ${eng.window_days || 30} days`,
      icon: Send, color: BRAND.primary, key: "sent",
    },
    {
      label: "Open rate",
      value: `${eng.open_rate || 0}%`,
      sub: "all sends — campaigns + automations",
      icon: MailOpen, color: BRAND.terracotta, key: "open",
    },
    {
      label: "Click rate",
      value: `${eng.click_rate || 0}%`,
      sub: "all sends — campaigns + automations",
      icon: MousePointerClick, color: BRAND.gold, key: "click",
    },
    {
      label: data.audience_name ? `Audience · ${data.audience_name}` : "Audience",
      value: (data.audience_size || 0).toLocaleString(),
      sub: `${data.audience_growth_30d >= 0 ? "+" : ""}${data.audience_growth_30d || 0} this period`,
      icon: Users, color: BRAND.sage, key: "audience",
    },
  ];

  return (
    <div className="space-y-8" data-testid="mailchimp-dashboard">
      {disconnected && (
        <Card className="rounded-2xl bg-peach/20 border border-terracotta/20 shadow-warm p-5 flex items-start gap-4" data-testid="mailchimp-disconnected-banner">
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">Mailchimp isn't connected yet</p>
            <p className="text-sm text-brand-primary/70 mt-1">
              Add your Mailchimp API key in <a href="/settings" className="text-terracotta hover:underline">Settings → Integrations</a> to show live campaign metrics here.
            </p>
          </div>
        </Card>
      )}
      {errored && (
        <Card className="rounded-2xl bg-peach/20 border border-terracotta/40 shadow-warm p-5 flex items-start gap-4" data-testid="mailchimp-error-banner">
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">Couldn't reach Mailchimp</p>
            <p className="text-sm text-brand-primary/70 mt-1">{data.error || "Unknown error"}</p>
          </div>
        </Card>
      )}

      {/* Overall Engagement — last 30 days across campaigns + automations */}
      <div>
        <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Overall engagement</p>
            <h3 className="font-heading text-2xl text-brand-primary mt-1">Last 30 days · all sends</h3>
            <p className="text-xs text-brand-primary/55 mt-1">Campaigns, automations, and customer journeys — weighted by email volume.</p>
          </div>
          <DashboardMeta
            fetchedAt={data.fetched_at}
            loading={loading}
            error={data.source === "error" ? data.error : null}
            connected={data.source === "mailchimp" ? true : (data.source === "not_connected" ? false : undefined)}
            connectedLabel="Mailchimp not connected"
            onRefresh={() => load({ force: true })}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c) => (
            <Card
              key={c.key}
              data-testid={`mailchimp-stat-${c.key}`}
              className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${c.color}1A` }}>
                  <c.icon className="h-5 w-5" style={{ color: c.color }} strokeWidth={1.7} />
                </div>
                <p className="text-sm text-brand-primary/70 truncate">{c.label}</p>
              </div>
              <p className="font-heading text-4xl font-light text-brand-primary tracking-tight">{c.value}</p>
              {c.sub && <p className="text-[11px] uppercase tracking-wider text-brand-primary/50 mt-2 font-semibold">{c.sub}</p>}
            </Card>
          ))}
        </div>
      </div>

      {/* Send-type breakdown: campaigns vs automations */}
      {Object.keys(eng.by_type || {}).length > 0 && (
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6" data-testid="mailchimp-engagement-breakdown">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">By send type</p>
              <h3 className="font-heading text-xl text-brand-primary mt-1">Where engagement is coming from</h3>
            </div>
            <Badge className="rounded-full bg-sage/20 text-brand-primary border-0 text-[11px]">
              {(eng.total_sends || 0).toLocaleString()} emails · last 30 days
            </Badge>
          </div>
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-brand-primary/55">
                <th className="py-2 pr-4 font-medium">Type</th>
                <th className="py-2 pr-4 font-medium"># sends</th>
                <th className="py-2 pr-4 font-medium">Emails delivered</th>
                <th className="py-2 pr-4 font-medium">Open rate</th>
                <th className="py-2 pr-4 font-medium">Click rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-primary/10">
              {Object.entries(eng.by_type).map(([t, v]) => (
                <tr key={t}>
                  <td className="py-3 pr-4 text-brand-primary capitalize">{t.replace(/_/g, " ")}</td>
                  <td className="py-3 pr-4 text-brand-primary/70">{v.count}</td>
                  <td className="py-3 pr-4 text-brand-primary/70">{v.sends.toLocaleString()}</td>
                  <td className="py-3 pr-4">
                    <Badge className="rounded-full bg-sage/20 text-brand-primary border-0">{v.open_rate}%</Badge>
                  </td>
                  <td className="py-3 pr-4">
                    <Badge className="rounded-full bg-gold/20 text-brand-primary border-0">{v.click_rate}%</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.sms_note && (
            <p className="text-[11px] text-brand-primary/50 mt-4 flex items-start gap-1.5">
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>{data.sms_note}</span>
            </p>
          )}
        </Card>
      )}

      {/* Growth chart */}
      {(data.growth_trend || []).length > 0 && (
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6" data-testid="mailchimp-growth-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">Audience growth</p>
              <h3 className="font-heading text-xl text-brand-primary mt-1">Last 12 months of subscribers</h3>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.growth_trend}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND.terracotta} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={BRAND.terracotta} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#42525220" />
                <XAxis dataKey="week" stroke="#42525280" fontSize={12} />
                <YAxis stroke="#42525280" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #4252521a" }} />
                <Area type="monotone" dataKey="subscribers" stroke={BRAND.terracotta} strokeWidth={2.5} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Recent campaigns */}
      <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="recent-campaigns">
        <div className="px-6 py-5 flex items-center justify-between border-b border-brand-primary/10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">Recent campaigns</p>
            <h3 className="font-heading text-xl text-brand-primary mt-1">Last 5 sends</h3>
          </div>
          <a
            href={connected ? "https://us10.admin.mailchimp.com/" : "https://mailchimp.com/"}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-terracotta hover:underline"
            data-testid="open-mailchimp-link"
          >
            Open Mailchimp <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        {(data.recent_campaigns || []).length === 0 ? (
          <div className="p-8 text-center text-sm text-brand-primary/55">
            {connected ? "No sent campaigns yet for this audience." : "Connect Mailchimp to see campaigns."}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-brand-primary/55 bg-cream/50">
                <th className="px-6 py-3 font-medium">Campaign</th>
                <th className="px-6 py-3 font-medium">Sent</th>
                <th className="px-6 py-3 font-medium">Sends</th>
                <th className="px-6 py-3 font-medium">Open</th>
                <th className="px-6 py-3 font-medium">Click</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-primary/10">
              {data.recent_campaigns.map((c, i) => (
                <tr key={i} className="hover:bg-cream/40 transition">
                  <td className="px-6 py-4 text-brand-primary font-medium">{c.name}</td>
                  <td className="px-6 py-4 text-brand-primary/70">{c.sent}</td>
                  <td className="px-6 py-4 text-brand-primary/70">{(c.sends || 0).toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <Badge className="rounded-full bg-sage/20 text-brand-primary border-0">{c.open_rate}%</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Badge className="rounded-full bg-gold/20 text-brand-primary border-0">{c.click_rate}%</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Newsletter submissions */}
      <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="newsletter-submissions">
        <div className="px-6 py-5 flex items-center justify-between border-b border-brand-primary/10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">Newsletter submissions</p>
            <h3 className="font-heading text-xl text-brand-primary mt-1">Staff items for the next send</h3>
            {canChangeStatus && (
              <p className="text-xs text-brand-primary/55 mt-1 inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-sage" />
                You can approve, reject, or mark submissions as added to the newsletter.
              </p>
            )}
          </div>
          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-submission-button"
                onClick={openNew}
                className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-10 px-5 shadow-warm"
              >
                <Plus className="h-4 w-4 mr-1.5" /> New submission
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? "Edit submission" : "New newsletter submission"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <label className="text-xs uppercase tracking-wider text-brand-primary/60">Title</label>
                  <Input
                    data-testid="submission-title-input"
                    placeholder="Headline for the newsletter"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1 rounded-xl bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-brand-primary/60">Body</label>
                  <Textarea
                    data-testid="submission-body-input"
                    rows={6}
                    placeholder="What should go in the newsletter?"
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    className="mt-1 rounded-xl bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-brand-primary/60">Notes (optional)</label>
                  <Textarea
                    data-testid="submission-notes-input"
                    rows={2}
                    placeholder="Links, timing, contact person…"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="mt-1 rounded-xl bg-white"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="submission-save-button"
                  onClick={submitForm}
                  className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white"
                >
                  {editing ? "Save changes" : "Add submission"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {submissions.length === 0 ? (
          <div className="p-10 text-center text-sm text-brand-primary/55" data-testid="no-submissions">
            <Send className="h-8 w-8 mx-auto text-terracotta/50 mb-2" strokeWidth={1.5} />
            No submissions yet. Click <span className="font-medium text-terracotta">New submission</span> to add one.
          </div>
        ) : (
          <ul className="divide-y divide-brand-primary/10" data-testid="submissions-list">
            {submissions.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.pending;
              const canManage = user?.role === "admin" || user?.newsletter_editor || s.author_id === user?.user_id;
              return (
                <li
                  key={s.id}
                  data-testid={`submission-${s.id}`}
                  className="p-5 hover:bg-cream/40 transition"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-brand-primary">{s.title}</p>
                        <Badge
                          className={`rounded-full border-0 text-[10px] uppercase tracking-wider ${meta.color}`}
                          data-testid={`submission-status-${s.id}`}
                        >
                          {meta.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-brand-primary/70 mt-1 whitespace-pre-wrap line-clamp-3">{s.body}</p>
                      {s.notes && (
                        <p className="text-xs text-brand-primary/50 mt-1 italic">Note: {s.notes}</p>
                      )}
                      <p className="text-[10px] uppercase tracking-wider text-brand-primary/45 mt-2 inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        {s.author_name} · {formatDistanceToNow(parseISO(s.created_at), { addSuffix: true })}
                        {s.status_changed_at && s.status !== "pending" && (
                          <> · {meta.label.toLowerCase()} {format(parseISO(s.status_changed_at), "MMM d")}</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {canChangeStatus && (
                        <Select
                          value={s.status}
                          onValueChange={(v) => changeStatus(s, v)}
                        >
                          <SelectTrigger
                            data-testid={`submission-status-select-${s.id}`}
                            className="w-48 rounded-full bg-white border-brand-primary/15 h-9"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="added_to_newsletter">Added to newsletter</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {canManage && (
                        <>
                          <button
                            onClick={() => openEdit(s)}
                            data-testid={`submission-edit-${s.id}`}
                            className="h-9 w-9 rounded-lg grid place-items-center text-brand-primary/60 hover:text-terracotta hover:bg-cream"
                            aria-label="Edit submission"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteSub(s)}
                            data-testid={`submission-delete-${s.id}`}
                            className="h-9 w-9 rounded-lg grid place-items-center text-brand-primary/60 hover:text-terracotta hover:bg-cream"
                            aria-label="Delete submission"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
